import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCompletedOrders } from '@saiens/shared/hooks';
import { formatDate } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Search, ChevronRight, MapPin, Calendar } from 'lucide-react';

export function AcceptanceListPage() {
  const [search, setSearch] = useState('');
  const [myOrders, setMyOrders] = useState(true);
  const [excludeInvoiced, setExcludeInvoiced] = useState(true);
  const [hasCompletionDate, setHasCompletionDate] = useState(true);

  const { data, isLoading, error } = useCompletedOrders({
    search,
    myOrders: myOrders || undefined,
    excludeInvoiced,
    hasCompletionDate,
    limit: 50,
  });

  return (
    <div className="px-4 py-6">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900">待驗收案件</h1>
        <p className="text-sm text-gray-500">已完工且尚未驗收的案件</p>
      </div>

      <div className="mb-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜尋訂單編號、案名、客戶..."
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-primary focus:outline-none"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <FilterChip
            label="全部"
            active={!myOrders}
            onClick={() => setMyOrders(false)}
          />
          <FilterChip
            label="我的案件"
            active={myOrders}
            onClick={() => setMyOrders(true)}
          />
          <FilterChip
            label="排除已開立憑單"
            active={excludeInvoiced}
            onClick={() => setExcludeInvoiced((prev) => !prev)}
          />
          <FilterChip
            label="有完工日"
            active={hasCompletionDate}
            onClick={() => setHasCompletionDate((prev) => !prev)}
          />
          <Link
            to="/acceptance/payment-status"
            className="ml-auto whitespace-nowrap rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-200"
          >
            付款追蹤
          </Link>
          <Link
            to="/acceptance/manage"
            className="whitespace-nowrap rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-200"
          >
            驗收管理
          </Link>
        </div>
      </div>

      <div className="rounded-xl bg-white shadow-sm">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-500">載入失敗</div>
        ) : data?.items.length === 0 ? (
          <div className="py-12 text-center text-gray-500">沒有待驗收的案件</div>
        ) : (
          <div className="divide-y">
            {data?.items.map((order) => (
              <Link
                key={order.id}
                to={`/acceptance/create/${order.id}`}
                className="flex items-center justify-between p-4 hover:bg-gray-50"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900">
                    {order.project_name || order.name}
                  </p>
                  <p className="truncate text-sm text-gray-500">
                    {order.partner_name || '---'}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
                    {order.project_addr && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {order.project_addr}
                      </span>
                    )}
                    {order.completion_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        完工: {formatDate(order.completion_date)}
                      </span>
                    )}
                    {order.construction_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        施工: {formatDate(order.construction_date)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="ml-4 flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">
                    NT${order.amount_total?.toLocaleString()}
                  </span>
                  <ChevronRight className="h-5 w-5 text-gray-300" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {data && (
        <p className="mt-4 text-center text-sm text-gray-500">
          共 {data.total} 筆待驗收案件
        </p>
      )}
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? 'bg-primary text-white'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}
